import {noop} from '@benchling/js-utils';
import React, {useState, useRef} from 'react';

import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import SearchInput from 'search/inputs/SearchInput';
import FilesPickerComponent from 'ui-library/FilesPickerComponent';
import Modal from 'ui-library/modals/Modal';
import {Tab, TabContainer} from 'ui-library/TabContainer';

interface SelectPipelineFilesModalProps {
  onRequestSubmit: () => void;
  onRequestClose: () => void;
  title: string;
  fileIds: Array<string>;
  isMulti: boolean;
  onSelectFiles: (newFileIds: Array<string>) => void;
  onUploadFiles: (files: Array<File>) => JQueryPromise<void>;
}

enum TabType {
  DATA_CATALOG = 'data_catalog',
  UPLOAD = 'upload',
}

export default function SelectPipelineFilesModal({
  title,
  fileIds,
  onSelectFiles,
  onRequestClose,
  isMulti,
  onUploadFiles,
}: SelectPipelineFilesModalProps): JSX.Element {
  const [activeTab, setActiveTab] = useState<TabType>(TabType.DATA_CATALOG);
  const [selectedFileIds, setSelectedFileIds] = useState<Array<string>>(fileIds);

  const fileInputRef = useRef<FilesPickerComponent>(null);
  const handleSubmit = (): void => {
    if (activeTab === TabType.UPLOAD) {
      // If tab is UPLOAD, then use the FilesPickerComponent's ref to handle the upload then close the modal
      assert(fileInputRef.current != null, 'Expected non-null fileInputRef.current');
      fileInputRef.current.handleUpload({allComplete: onRequestClose, uploadComplete: noop});
    } else {
      // Otherwise just set the file IDs and close
      onSelectFiles(selectedFileIds);
      onRequestClose();
    }
  };

  return (
    <Modal
      enableRedesign={true}
      title={title}
      onRequestClose={onRequestClose}
      onRequestSubmit={handleSubmit}
      buttons={[
        {text: 'Cancel', cancel: true},
        {
          text: 'Select',
          submit: true,
          disabled: false,
        },
      ]}
    >
      <TabContainer<TabType> activeTab={activeTab} onRequestTabChange={setActiveTab}>
        <Tab tabKey={TabType.DATA_CATALOG} title="Select from data catalog">
          <SearchInput
            autoFocus={false}
            types={[ModelTypeConstants.FILE]}
            selected={selectedFileIds}
            onSelect={(fileId) => {
              if (fileId == null) {
                return;
              }
              if (isMulti) {
                setSelectedFileIds([...selectedFileIds, fileId]);
              } else {
                setSelectedFileIds([fileId]);
              }
            }}
            onRemove={(fileId) => {
              if (fileId == null) {
                return;
              }
              setSelectedFileIds(selectedFileIds.filter((id) => id !== fileId));
            }}
            placeholder="Search for files..."
            isMulti={isMulti}
          />
        </Tab>
        <Tab tabKey={TabType.UPLOAD} title={`Upload file${isMulti ? 's' : ''}`}>
          <FilesPickerComponent
            multiple={isMulti}
            upload={onUploadFiles}
            bulkUpload={true}
            ref={fileInputRef}
          />
        </Tab>
      </TabContainer>
    </Modal>
  );
}
